import { useCallback, useEffect, useRef, useState } from 'react';
import { useToolbarControllerContext } from './ToolbarController';
import { TToolbarIconButton } from './types';
import { useMount, usePrevious } from '@apia/util';
import { IconButton } from '../forms';

export const ToolbarIconButton = ({
  id,
  action,
  actionSource,
  allowToggle,
  className,
  defaultToggled,
  disabled,
  icon,
  iconSize,
  isLoading: outerIsLoading,
  title,
  toggled: toggledProp,
}: TToolbarIconButton) => {
  const [innerIsToggled, setInnerIsToggled] = useState<boolean>(
    toggledProp ?? (allowToggle ? defaultToggled ?? false : false),
  );
  const [isDisabled, setIsDisabled] = useState(!!disabled);
  const context = useToolbarControllerContext();
  const [isLoading, setIsLoading] = useState(false);

  const previousIsLoading = usePrevious(outerIsLoading);
  if (previousIsLoading.current !== outerIsLoading)
    setIsLoading(!!outerIsLoading);

  const lastDisabledProp = usePrevious(disabled);
  if (lastDisabledProp.current !== disabled) {
    setIsDisabled(!!disabled);
  }

  /**
   * Si la prop toggled cambia y no está undefined, se debe actualizar el
   * estado interno.
   */
  const lastToggledProp = useRef<boolean | undefined>(toggledProp);
  if (
    toggledProp !== undefined &&
    innerIsToggled !== toggledProp &&
    lastToggledProp.current !== toggledProp
  ) {
    lastToggledProp.current = toggledProp;
    setInnerIsToggled(toggledProp);
  }

  const handleClick = useCallback(async () => {
    setIsLoading(true);
    try {
      if (lastToggledProp.current !== undefined) {
        await action(
          { isToggled: !lastToggledProp.current, isLoading },
          context,
        );
      } else if (allowToggle) {
        let isToggled = false;
        setInnerIsToggled((current) => {
          isToggled = !current;
          return isToggled;
        });
        await action({ isToggled, isLoading }, context);
      } else {
        await action({ isToggled: false, isLoading }, context);
      }
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }, [action, allowToggle, context, isLoading]);

  /**
   * Se maneja el uso de teclado para hacer click sobre el elemento
   */
  useEffect(() => {
    const unsuscribe1 = context.on('updateItemState', (evId) => {
      if (evId === id) {
        setInnerIsToggled(!!context.itemsState[evId].toggled);
        setIsDisabled(!!context.itemsState[evId].disabled);
      }
    });
    const unsuscribe2 = context.on('pressEnter', (evId) => {
      if (evId === id) void handleClick();
    });

    return () => {
      unsuscribe1();
      unsuscribe2();
    };
  }, [context, id, handleClick]);

  useMount(() => {
    if (context.itemsState[id]) {
      if (context.itemsState[id].disabled !== undefined) {
        setIsDisabled(!!context.itemsState[id].disabled);
      }
      if (context.itemsState[id].toggled !== undefined) {
        setInnerIsToggled(!!context.itemsState[id].toggled);
      }
    } else
      context.itemsState[id] = {
        disabled: isDisabled,
        toggled: innerIsToggled,
        ...context.itemsState[id],
      };
  });

  return (
    <IconButton
      className={`toolbar__button ${innerIsToggled ? 'toggled' : ''} ${className ?? ''}`}
      data-id={id}
      disabled={isDisabled}
      icon={icon}
      onClick={actionSource !== 'mousedown' ? handleClick : undefined}
      isLoading={isLoading}
      onMouseDown={actionSource === 'mousedown' ? handleClick : undefined}
      size={iconSize ?? 'Md'}
      title={title}
      variant="icon-outline"
    />
  );
};
